
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, CheckCircle, ArrowLeft } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

const EmailVerified = () => {
  const [searchParams] = useSearchParams();
  const [userRole, setUserRole] = useState<string | null>(null);

  // Get role from URL params
  useEffect(() => {
    const role = searchParams.get('role');
    if (role) {
      setUserRole(role);
    }
  }, [searchParams]);

  const getLoginRoute = () => {
    // Route based on role if available
    if (userRole === 'tenant') {
      return '/tenant-portal/login';
    }
    // Default to landlord login
    return '/login';
  };

  const getLoginText = () => {
    if (userRole === 'tenant') {
      return 'Sign in to Tenant Portal';
    }
    return 'Sign in to Dashboard';
  };

  const getWelcomeMessage = () => {
    if (userRole === 'tenant') {
      return 'Your tenant account has been verified successfully!';
    }
    return 'Your landlord account has been verified successfully!';
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <Card className="shadow-lg border border-border bg-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Building2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-foreground">RentFlow</span>
            </div>
            
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
              ✅ Email Verified Successfully!
            </CardTitle>
            <CardDescription className="text-muted-foreground text-sm sm:text-base">
              {getWelcomeMessage()}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Success message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-800 font-medium">
                Account verification complete
              </p>
              <p className="text-green-600 text-sm mt-1">
                Please sign in to continue and access your account
              </p>
            </div>

            {/* Login button */}
            <div className="space-y-3">
              <Button 
                asChild 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Link to={getLoginRoute()}>
                  {getLoginText()}
                </Link>
              </Button>

              {/* Show alternative login option if we don't know the role */}
              {!userRole && (
                <Button 
                  asChild 
                  variant="outline"
                  className="w-full"
                >
                  <Link to="/tenant-portal/login">
                    Sign in as Tenant
                  </Link>
                </Button>
              )}
            </div>

            {/* Security notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-blue-800 text-sm font-medium">
                🔒 Security Notice
              </p>
              <p className="text-blue-600 text-xs mt-1">
                For your security, you were not automatically signed in. Please use the button above to sign in.
              </p>
            </div>

            {/* Additional help */}
            <div className="text-center pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Having trouble signing in?{" "}
                <Link to="/reset-password" className="text-primary hover:text-primary/80 font-medium">
                  Reset your password
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerified;
